'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Sun, Moon, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useThemeStore, useIsDark } from '@/stores/themeStore';
import { AxisLogo, AxisWordmark } from '@/components/ui/CoreOpsLogo';
import { Footer } from './Footer';

interface PublicPageLayoutProps {
  children: React.ReactNode;
}

const PublicPageLayout = ({ children }: PublicPageLayoutProps) => {
  const { setTheme, toggleTheme } = useThemeStore();
  const isDark = useIsDark();
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Default to light mode on first visit
  useEffect(() => {
    const stored = localStorage.getItem('axis-theme');
    if (!stored) setTheme('light');
  }, [setTheme]);

  // Sync status bar color with theme (iOS Safari)
  useEffect(() => {
    const color = isDark ? '#0d0d0d' : '#FAFBFC';
    document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
      meta.setAttribute('content', color);
      meta.removeAttribute('media');
    });
  }, [isDark]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  const navLinks = [
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'Features', href: '/features' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'FAQ', href: '/faq' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFBFC] text-gray-900 dark:bg-[#0d0d0d] dark:text-secondary-100">
      {/* Navigation */}
      <nav className={cn(
        "fixed top-0 left-0 right-0 z-50",
        "bg-white/80 backdrop-blur-xl dark:bg-[#0d0d0d]/80",
        "border-b border-gray-200/50 dark:border-white/[0.04]"
      )}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3">
              <AxisLogo size="md" />
              <AxisWordmark size="md" />
            </Link>

            {/* Nav Links — Desktop */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={cn(
                    "text-sm transition-colors",
                    pathname === link.href
                      ? "text-primary-600 dark:text-primary-400"
                      : "text-gray-500 hover:text-gray-900 dark:text-secondary-400 dark:hover:text-secondary-100"
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTAs + Mobile Menu Toggle */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={toggleTheme}
                className={cn(
                  "p-2 rounded-lg transition-all duration-200",
                  "text-gray-400 hover:text-gray-600 dark:text-secondary-500 dark:hover:text-secondary-300",
                  "hover:bg-gray-100 dark:hover:bg-white/[0.06]"
                )}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <Link
                href="/login"
                className="hidden sm:inline text-sm text-gray-500 hover:text-gray-900 dark:text-secondary-400 dark:hover:text-secondary-100 transition-colors"
              >
                Login
              </Link>
              <Link
                href="/#contact"
                className={cn(
                  "hidden sm:inline-block px-4 py-2 rounded-lg text-sm font-medium",
                  "bg-primary-600 text-white dark:bg-primary-500",
                  "hover:bg-primary-500 dark:hover:bg-primary-400 transition-all duration-300",
                  "shadow-md dark:shadow-[0_0_20px_-5px_rgba(59,130,246,0.4)]"
                )}
              >
                Join the Waitlist
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={cn(
                  "md:hidden p-2 rounded-lg transition-all duration-200",
                  "text-gray-500 hover:text-gray-700 dark:text-secondary-400 dark:hover:text-secondary-200",
                  "hover:bg-gray-100 dark:hover:bg-white/[0.06]"
                )}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={cn(
          "md:hidden overflow-hidden transition-all duration-300 ease-out",
          "border-t border-gray-200/50 dark:border-white/[0.04]",
          mobileMenuOpen ? "max-h-80 opacity-100" : "max-h-0 opacity-0 border-t-0"
        )}>
          <div className="px-6 py-4 space-y-1 bg-white/95 dark:bg-[#0d0d0d]/95 backdrop-blur-xl">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "block py-3 text-sm font-medium transition-colors",
                  pathname === link.href
                    ? "text-primary-600 dark:text-primary-400"
                    : "text-gray-600 hover:text-gray-900 dark:text-secondary-300 dark:hover:text-secondary-100"
                )}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-gray-200 dark:border-white/[0.06] flex flex-col gap-3">
              <Link
                href="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-secondary-300 dark:hover:text-secondary-100 transition-colors"
              >
                Login
              </Link>
              <Link
                href="/#contact"
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "inline-block text-center px-4 py-2.5 rounded-lg text-sm font-medium",
                  "bg-primary-600 text-white dark:bg-primary-500",
                  "hover:bg-primary-500 dark:hover:bg-primary-400 transition-all duration-300"
                )}
              >
                Join the Waitlist
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-16">
        {children}
      </main>

      <Footer />
    </div>
  );
};

export { PublicPageLayout };
